/**
 * cards.tsx — the six FULL display cards (review/booking/payment × list/stats).
 * Each is purely presentational and reads its untrusted payload through the
 * defensive helpers in format.ts; a missing field just renders less, never throws.
 */
import React from 'react'
import { useTranslation } from 'react-i18next'
import { CopilotWidget } from '../lib/copilotTypes'
import { CardHeader, Stars, StatusBadge, domainGlyph } from './atoms'
import { CheckIcon } from '../icons'
import {
  isDirectSource,
  items,
  money,
  num,
  oneDecimal,
  orDefault,
  reviewSourceLabel,
  shortDate,
  starCount,
  statsData,
  statusLabel,
  statusTone,
  str,
  widgetDomain
} from './format'
import styles from './widgets.module.scss'

type FullCard = React.FC<{ widget: CopilotWidget }>

/** "Showing N of M" footer, only when the list was capped below the true total. */
const CapFooter: React.FC<{ shown: number; total?: number }> = ({ shown, total }) => {
  const { t } = useTranslation()
  if (total === undefined || total <= shown) return null
  return <div className={styles.capFooter}>{t('genAI.copilot-widget-showing', { shown, total })}</div>
}

/* ---------------- reviews ---------------- */

const ReviewList: FullCard = ({ widget }) => {
  const { t } = useTranslation()
  const dom = widgetDomain(widget.type)
  const rows = items(widget)
  const total = num(widget.count)
  return (
    <div className={styles.card}>
      <CardHeader
        color={dom.color}
        label={t(dom.labelKey)}
        glyph={domainGlyph(dom.key, styles.tileGlyph)}
        metric={total !== undefined ? total : rows.length}
      />
      <div className={styles.list}>
        {rows.map((it, i) => {
          const stars = starCount(it.rating)
          const author = str(it.author)
          const sourceLabel = reviewSourceLabel(it.source)
          const direct = isDirectSource(it.source)
          const when = shortDate(it.date)
          const text = str(it.text)
          // a verified tick (or platform label) stands in for the raw source token;
          // the raw shouty value ("DIRECT"/"GOOGLEMYBUSINESS") is never shown.
          const hasLead = Boolean(author) || direct
          return (
            <div key={i} className={styles.reviewRow} style={{ color: dom.color }}>
              <div className={styles.reviewTop}>
                {stars !== undefined && <Stars count={stars} />}
                <span className={styles.metaLine}>
                  {author}
                  {direct && (
                    <span
                      className={styles.verified}
                      role="img"
                      title={t('genAI.copilot-widget-direct-review')}
                      aria-label={t('genAI.copilot-widget-direct-review')}>
                      <CheckIcon className={styles.verifiedIcon} />
                    </span>
                  )}
                  {sourceLabel && (
                    <span>
                      {hasLead ? ' · ' : ''}
                      {sourceLabel}
                    </span>
                  )}
                  {when && (
                    <span className={styles.rowDate}>
                      {hasLead || sourceLabel ? ' · ' : ''}
                      {when}
                    </span>
                  )}
                </span>
              </div>
              {text && <div className={styles.reviewText}>{text}</div>}
            </div>
          )
        })}
      </div>
      <CapFooter shown={rows.length} total={total} />
    </div>
  )
}

const ReviewStats: FullCard = ({ widget }) => {
  const { t } = useTranslation()
  const dom = widgetDomain(widget.type)
  const d = statsData(widget)
  const avg = oneDecimal(d.avg_rating)
  const stars = starCount(d.avg_rating)
  const count = num(d.count)
  return (
    <div className={styles.card}>
      <CardHeader color={dom.color} label={t(dom.labelKey)} glyph={domainGlyph(dom.key, styles.tileGlyph)} />
      <div className={styles.statHero} style={{ color: dom.color }}>
        {avg && <span className={styles.statBig}>{avg}</span>}
        {stars !== undefined && <Stars count={stars} />}
      </div>
      {count !== undefined && (
        <div className={styles.statSub}>{t('genAI.copilot-widget-total-reviews', { count })}</div>
      )}
    </div>
  )
}

/* ---------------- bookings ---------------- */

const BookingList: FullCard = ({ widget }) => {
  const { t } = useTranslation()
  const dom = widgetDomain(widget.type)
  const rows = items(widget)
  const total = num(widget.count)
  return (
    <div className={styles.card}>
      <CardHeader
        color={dom.color}
        label={t(dom.labelKey)}
        glyph={domainGlyph(dom.key, styles.tileGlyph)}
        metric={total !== undefined ? total : rows.length}
      />
      <div className={styles.list}>
        {rows.map((it, i) => {
          const service = str(it.service)
          const customer = str(it.customer_name)
          const when = shortDate(it.start)
          const status = str(it.status)
          return (
            <div key={i} className={styles.lineRow}>
              <div className={styles.lineMain}>
                <span className={styles.lineTitle}>{service || customer || '—'}</span>
                <span className={styles.lineSub}>
                  {[service ? customer : undefined, when].filter(Boolean).join(' · ')}
                </span>
              </div>
              {status && <StatusBadge label={statusLabel(status, t)} tone={statusTone(status)} />}
            </div>
          )
        })}
      </div>
      <CapFooter shown={rows.length} total={total} />
    </div>
  )
}

const BookingStats: FullCard = ({ widget }) => {
  const { t } = useTranslation()
  const dom = widgetDomain(widget.type)
  const d = statsData(widget)
  // real shape is `{ total, by_status }`; keep upcoming_count/counts_by_status as fallbacks.
  const total = orDefault(num(d.total), num(d.upcoming_count))
  const period = str(d.period)
  const byStatusSource =
    d.by_status && typeof d.by_status === 'object' && !Array.isArray(d.by_status)
      ? d.by_status
      : d.counts_by_status && typeof d.counts_by_status === 'object' && !Array.isArray(d.counts_by_status)
      ? d.counts_by_status
      : {}
  const byStatus = byStatusSource as Record<string, unknown>
  const statusEntries = Object.keys(byStatus)
    .map((k) => ({ key: k, value: num(byStatus[k]) }))
    .filter((e) => e.value !== undefined)
  return (
    <div className={styles.card}>
      <CardHeader
        color={dom.color}
        label={t(dom.labelKey)}
        glyph={domainGlyph(dom.key, styles.tileGlyph)}
        metric={period || undefined}
      />
      {total !== undefined && (
        <div className={styles.statHero} style={{ color: dom.color }}>
          <span className={styles.statBig}>{total}</span>
          <span className={styles.statUnit}>{t('genAI.copilot-widget-bookings-unit')}</span>
        </div>
      )}
      {statusEntries.length > 0 && (
        <div className={styles.breakdown}>
          {statusEntries.map((e) => (
            <span key={e.key} className={styles.breakdownItem}>
              <span className={styles.breakdownValue}>{e.value}</span>
              {statusLabel(e.key, t)}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

/* ---------------- payments ---------------- */

const PaymentList: FullCard = ({ widget }) => {
  const { t } = useTranslation()
  const dom = widgetDomain(widget.type)
  const rows = items(widget)
  const total = num(widget.count)
  return (
    <div className={styles.card}>
      <CardHeader
        color={dom.color}
        label={t(dom.labelKey)}
        glyph={domainGlyph(dom.key, styles.tileGlyph)}
        metric={total !== undefined ? total : rows.length}
      />
      <div className={styles.list}>
        {rows.map((it, i) => {
          const customer = str(it.customer_name) || str(it.customer_email)
          const amount = money(it.amount)
          const when = shortDate(it.paid_date)
          const status = str(it.status)
          return (
            <div key={i} className={styles.lineRow}>
              <div className={styles.lineMain}>
                <span className={styles.lineTitle}>{customer || '—'}</span>
                <span className={styles.lineSub}>
                  {when}
                  {status && when ? ' · ' : ''}
                  {status && <StatusBadge label={statusLabel(status, t)} tone={statusTone(status)} />}
                </span>
              </div>
              {amount && <span className={styles.amount}>{amount}</span>}
            </div>
          )
        })}
      </div>
      <CapFooter shown={rows.length} total={total} />
    </div>
  )
}

const PaymentStats: FullCard = ({ widget }) => {
  const { t } = useTranslation()
  const dom = widgetDomain(widget.type)
  // payments nest their numbers under `.data`; statsData merges them up.
  const d = statsData(widget)
  const total = (() => {
    for (const k of ['total', 'total_amount', 'gross_amount', 'gross', 'amount', 'collected', 'net']) {
      const v = num(d[k])
      if (v !== undefined) return v
    }
    return undefined
  })()
  const count = (() => {
    for (const k of ['count', 'transactions', 'success_count', 'total_transactions']) {
      const v = num(d[k])
      if (v !== undefined) return v
    }
    return undefined
  })()
  return (
    <div className={styles.card}>
      <CardHeader color={dom.color} label={t(dom.labelKey)} glyph={domainGlyph(dom.key, styles.tileGlyph)} />
      {total !== undefined && (
        <div className={styles.statHero} style={{ color: dom.color }}>
          <span className={styles.statBig}>{money(total)}</span>
          <span className={styles.statUnit}>{t('genAI.copilot-widget-collected')}</span>
        </div>
      )}
      {count !== undefined && (
        <div className={styles.statSub}>{t('genAI.copilot-widget-payments-count', { count })}</div>
      )}
    </div>
  )
}

/** type → full card. Unknown types never reach here (partition drops them). */
export const FULL_CARDS: Record<string, FullCard> = {
  review_list: ReviewList,
  review_stats: ReviewStats,
  booking_list: BookingList,
  booking_stats: BookingStats,
  payment_list: PaymentList,
  payment_stats: PaymentStats
}
